// Revenue Budget Review — the Overview VoltaGrid tab's LEMSO check of the
// cost side against the billable side, class by class (Seth 11/2: "see where
// we are on budget for cost but off on billable rev").
//
// Every figure is the Cost Plus Forecast tab's (helpers/costPlusForecast
// costPlusModel on the same inputs), so the two pages never disagree:
//   cost     — Day Zero + approved CO budget; JDE cost to date; EAC = JTD +
//              open POs + the forecast's ETC
//   billable — the Revenue Budget (the Day Zero's Billable Cost column);
//              the Billing tab's priced lines to date; EAC = that plus the
//              cost to go priced by the contract rules
// A class is "on" when its EAC sits within TOL of its budget; the status
// word says where the cost side and the billable side each stand, and the
// one to look for is "On cost, billing short": spending to plan, billing
// under it. Cost types open under each class to show which line it is.
// Export hands the same rows out as a workbook (voltaGridRevenueReviewXlsx).
import React, { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { fNum } from '../db/company';
import { jobOpenCommitmentsAtom, jobCostDetailAtom, projectCompleteAtom } from '../store/company';
import { jobPlanByCode } from '../helpers/jobBudgetEac';
import { costPlusModel, ratioText } from '../helpers/costPlusForecast';
import { reviewRows, statusOf, subLabel, TOL } from '../helpers/revenueReview';
import { downloadRevenueReview } from './voltaGridRevenueReviewXlsx';
import { showToast } from '../helpers/toast';

const toneColors = (palette, tone) => {
    if (tone === 'good') return { color: '#166534', background: palette.greenLight };
    if (tone === 'caution') return { color: '#854d0e', background: palette.orangeLight };
    if (tone === 'bad') return { color: '#b02329', background: palette.redLight };
    if (tone === 'info') return { color: palette.blue700, background: palette.blue50 };
    return { color: palette.muted, background: '#f1f5f9' };
};

const money = (n) => (n == null ? '…' : n ? `${n < 0 ? '-' : ''}$${fNum(Math.abs(n), 1)}` : '-');
const signed = (n) => (n == null ? '…' : Math.abs(n) < 0.5 ? '-' : `${n > 0 ? '+' : '-'}$${fNum(Math.abs(n), 1)}`);
const pct = (num, den) => (num == null || !(den > 0) ? '…' : `${(num / den * 100).toFixed(0)}%`);
const varText = (v) => (v == null ? '' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);

function VoltaGridRevenueReview({ palette, jobUserData, selectedJob, jobName, selectedVersion, versionStart, billRev }) {
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [projectComplete] = useAtom(projectCompleteAtom);
    const [open, setOpen] = useState({});
    const [exporting, setExporting] = useState(false);

    // The cost to go per code per class, the Overview's rules — the same
    // call the Cost Plus Forecast makes.
    const plan = useMemo(() => {
        if (!selectedVersion || !Array.isArray(jobUserData) || !Array.isArray(jobOpenCommitments)) return null;
        const mine = jobUserData.filter((d) => String(d?.BU_ID || '').trim() === String(selectedJob || '').trim());
        if (!mine.length) return null;
        return jobPlanByCode({ userData: mine, version: selectedVersion, openCommitments: jobOpenCommitments, versionStart, projectComplete });
    }, [jobUserData, jobOpenCommitments, selectedVersion, versionStart, projectComplete, selectedJob]);

    const model = useMemo(() => costPlusModel({
        userData: jobUserData, version: selectedVersion, job: selectedJob, plan, openCommitments: jobOpenCommitments, jobCostDetail,
        txns: billRev?.txns, fuelCtx: billRev?.fuelCtx, invoices: billRev?.invoices, priceCtx: billRev?.priceCtx, rateCfg: billRev?.rateCfg,
        ready: !!billRev?.ready, versionStart,
    }), [jobUserData, selectedVersion, selectedJob, plan, jobOpenCommitments, jobCostDetail, billRev, versionStart]);

    const ready = !!billRev?.ready;
    const planReady = !!model.planReady;
    const { rows, total } = reviewRows(model, { ready, planReady });

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const name = await downloadRevenueReview(model, { ready, planReady }, { job: selectedJob, jobName, versionDate: versionStart });
            showToast(`Downloaded ${name}`, 'success');
        } catch (e) {
            showToast(`Export failed: ${e?.message || 'error'}`, 'error');
        } finally {
            setExporting(false);
        }
    };

    const INK = '#1e293b';
    const th = { background: INK, color: '#fff', fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', padding: '6px 6px', textAlign: 'right', whiteSpace: 'nowrap' };
    const thL = { ...th, textAlign: 'left' };
    const thG = { ...th, textAlign: 'center', background: '#334155', borderBottom: '1px solid #475569' };
    const td = { fontSize: 11, color: palette.text, padding: '5px 6px', borderBottom: `1px solid ${palette.border}`, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' };
    const tdL = { ...td, textAlign: 'left' };
    const ft = { ...td, fontWeight: 700, borderTop: `1px solid ${INK}`, borderBottom: 'none', background: '#f8fafc' };
    const sep = { borderLeft: `2px solid ${palette.border}` };
    const tone = (d) => (d == null || Math.abs(d) < 0.5 ? palette.muted : d > 0 ? '#b91c1c' : '#15803d');
    const toneBill = (d) => (d == null || Math.abs(d) < 0.5 ? palette.muted : d < 0 ? '#b91c1c' : '#15803d');

    const statusCell = (r) => {
        const s = statusOf(r);
        const title = [s.costVar != null ? `Cost EAC ${varText(s.costVar)} vs budget` : '', s.billVar != null ? `Billable EAC ${varText(s.billVar)} vs budget` : ''].filter(Boolean).join(' · ');
        return <span title={title} style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, ...toneColors(palette, s.tone) }}>{s.word}</span>;
    };

    const cells = (r, { bold = false, nb = false } = {}) => {
        const w = bold ? { fontWeight: 700 } : {};
        return (
            <>
                <td style={{ ...td, ...w }}>{nb ? '-' : money(r.curCostBudget)}</td>
                <td style={{ ...td, ...w }}>{money(r.jtdCost)}</td>
                <td style={{ ...td, color: palette.muted }}>{nb ? '' : pct(r.jtdCost, r.curCostBudget)}</td>
                <td style={{ ...td, ...w }}>{money(r.costEacShown)}</td>
                <td style={{ ...td, ...w, color: nb ? palette.muted : tone(r.costEacShown == null ? null : r.costEacShown - r.curCostBudget) }}>{nb ? '' : r.costEacShown == null ? '…' : signed(r.costEacShown - r.curCostBudget)}</td>
                <td style={{ ...td, ...w, ...sep }}>{nb ? '-' : money(r.curBillBudget)}</td>
                <td style={{ ...td, ...w }}>{nb ? '-' : money(r.jtdBillShown)}</td>
                <td style={{ ...td, color: palette.muted }}>{nb ? '' : pct(r.jtdBillShown, r.curBillBudget)}</td>
                <td style={{ ...td, ...w }}>{nb ? '-' : money(r.billEacShown)}</td>
                <td style={{ ...td, ...w, color: nb ? palette.muted : toneBill(r.billEacShown == null ? null : r.billEacShown - r.curBillBudget) }}>{nb ? '' : r.billEacShown == null ? '…' : signed(r.billEacShown - r.curBillBudget)}</td>
                <td style={{ ...td, ...sep, color: palette.muted }} title="Bid: Revenue Budget ÷ cost budget → To date: billable to date ÷ cost to date">{nb ? '' : `${ratioText(r.bidRatio)} → ${ready ? ratioText(r.toDateRatio) : '…'}`}</td>
            </>
        );
    };
    // The class cell: its name, with where it stands under it.
    const labelCell = (r, text, { caret = null, pad = 0, muted = false } = {}) => (
        <td style={{ ...tdL, paddingLeft: 6 + pad, whiteSpace: 'normal', minWidth: 150 }}>
            <div style={{ display: 'flex', alignItems: 'center', fontWeight: muted ? 400 : 600, color: muted ? palette.muted : palette.text }}>{caret}{text}</div>
            <div style={{ marginTop: 2 }}>{statusCell(r)}</div>
        </td>
    );

    const short = rows.filter((r) => r.kind === 'class' && statusOf(r).word === 'On cost, billing short');

    return (
        <div className="vg-rev-review" style={{ background: palette.card, borderRadius: 10, border: `1px solid ${palette.border}`, boxShadow: '0 4px 16px rgba(15,23,42,.06)', padding: 16, marginBottom: 14 }}>
            <style>{`@media print { .vg-rev-review { page-break-inside: avoid !important; box-shadow: none !important; } }`}</style>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 11, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>Revenue Budget Review</h3>
                    <span style={{ fontSize: 11, color: palette.muted }}>Cost against its budget, billable against the Revenue Budget, by LEMSO class</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {ready && planReady && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, ...toneColors(palette, short.length ? 'caution' : 'good') }}>
                            {short.length ? `${short.length} ${short.length === 1 ? 'class is' : 'classes are'} on cost but billing short: ${short.map((r) => r.label).join(', ')}` : 'No class is on cost and billing short'}
                        </span>
                    )}
                    <button className="no-print" onClick={handleExport} disabled={exporting}
                        title="Download this table as Excel, with every cost type opened out, Committed and ETC on both sides, the rates, the margin and how each row's cost to go is priced"
                        style={{ padding: '3px 10px', fontSize: 11, cursor: exporting ? 'wait' : 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5, fontWeight: 600, opacity: exporting ? 0.6 : 1 }}>
                        {exporting ? 'Exporting…' : '⬇ Export'}
                    </button>
                </div>
            </div>

            {!model.hasRevBudget && (
                <div style={{ fontSize: 11, color: '#854d0e', background: palette.orangeLight, border: '1px solid rgba(161,98,7,.25)', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
                    This version has no Revenue Budget (the Day Zero's Billable Cost column), so the billable budget reads 0. Upload a Day Zero with a Billable Cost column to fill it.
                </div>
            )}

            <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                        <tr>
                            <th style={{ ...thG, background: INK }} />
                            <th style={thG} colSpan={5}>Cost</th>
                            <th style={{ ...thG, ...sep }} colSpan={5}>Billable</th>
                            <th style={{ ...thG, ...sep }} />
                        </tr>
                        <tr>
                            <th style={thL}>Class</th>
                            <th style={th}>Budget</th>
                            <th style={th}>JTD</th>
                            <th style={th}>% Spent</th>
                            <th style={th}>EAC</th>
                            <th style={th}>Δ Budget</th>
                            <th style={{ ...th, ...sep }}>Budget</th>
                            <th style={th}>JTD</th>
                            <th style={th}>% Earned</th>
                            <th style={th}>EAC</th>
                            <th style={th}>Δ Budget</th>
                            <th style={{ ...th, ...sep }}>Bills at</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const isOpen = !!open[r.key];
                            const canOpen = r.subs.length > 0;
                            return (
                                <React.Fragment key={r.key}>
                                    <tr style={{ cursor: canOpen ? 'pointer' : 'default' }} onClick={() => { if (canOpen) setOpen((p) => ({ ...p, [r.key]: !p[r.key] })); }}>
                                        {labelCell(r, r.label, { caret: <span style={{ display: 'inline-block', width: 12, color: palette.muted }}>{canOpen ? (isOpen ? '▾' : '▸') : ''}</span> })}
                                        {cells(r, { bold: true, nb: r.kind === 'nb' })}
                                    </tr>
                                    {isOpen && r.subs.map((s) => (
                                        <tr key={`${r.key}|${s.key || s.ct}`} style={{ background: '#fafbfc' }}>
                                            {labelCell(s, subLabel(s), { pad: 18, muted: true })}
                                            {cells(s)}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td style={{ ...ft, textAlign: 'left', whiteSpace: 'normal' }}>
                                <div>Total Costs</div>
                                <div style={{ marginTop: 2, fontWeight: 400 }}>{statusCell(total)}</div>
                            </td>
                            <td style={ft}>{money(total.curCostBudget)}</td>
                            <td style={ft}>{money(total.jtdCost)}</td>
                            <td style={{ ...ft, color: palette.muted, fontWeight: 500 }}>{pct(total.jtdCost, total.curCostBudget)}</td>
                            <td style={ft}>{money(total.costEacShown)}</td>
                            <td style={{ ...ft, color: tone(total.costEacShown == null ? null : total.costEacShown - total.curCostBudget) }}>{total.costEacShown == null ? '…' : signed(total.costEacShown - total.curCostBudget)}</td>
                            <td style={{ ...ft, ...sep }}>{money(total.curBillBudget)}</td>
                            <td style={ft}>{money(total.jtdBillShown)}</td>
                            <td style={{ ...ft, color: palette.muted, fontWeight: 500 }}>{pct(total.jtdBillShown, total.curBillBudget)}</td>
                            <td style={ft}>{money(total.billEacShown)}</td>
                            <td style={{ ...ft, color: toneBill(total.billEacShown == null ? null : total.billEacShown - total.curBillBudget) }}>{total.billEacShown == null ? '…' : signed(total.billEacShown - total.curBillBudget)}</td>
                            <td style={{ ...ft, ...sep, color: palette.muted, fontWeight: 500 }}>{`${ratioText(total.bidRatio)} → ${ready ? ratioText(total.toDateRatio) : '…'}`}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div style={{ marginTop: 8, fontSize: 11, color: palette.muted, lineHeight: 1.5 }}>
                Same figures as the Cost Plus Forecast tab. Cost EAC = JDE cost to date + open POs + the forecast's ETC; billable EAC = the Billing tab's priced lines to date + the cost to go priced by the contract rules.
                Not-billable cost sits on its own row with its budget, so a class on budget reads on budget. Within ±{(TOL * 100).toFixed(0)}% of budget reads "on"; "billing short" is a billable EAC more than that under its budget.
                {' '}Bills at: the bid (Revenue Budget ÷ cost budget) → to date (billable to date ÷ cost to date). Export opens every cost type out with Committed, ETC, the rates, the margin and how the cost to go is priced.
            </div>
        </div>
    );
}

export default VoltaGridRevenueReview;
